import { useId, useMemo, useState, type FormEvent } from 'react';

import Select from '@/components/Select/Select';
import Switch from '@/components/Switch/Switch';
import useDashboardWidgetMutations from '@/features/dashboard/hooks/useDashboardWidgetMutations';
import type { WidgetId } from '@/features/dashboard/registry';
import { cn } from '@/lib/cn';
import { inputClasses } from '@/lib/formStyles';

import {
  customAssetId,
  KNOWN_ASSETS,
  mergeStocksConfig,
  tileCapacity,
  WIDGET_SIZES,
  type CustomAsset,
  type StocksLayout,
  type StocksWidgetConfig,
  type WatchlistEntry,
} from './config';

const CUSTOM_SYMBOL_RE = /^[A-Za-z0-9.=^-]{1,20}$/;

const ArrowUpIcon = (
  <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className="h-3.5 w-3.5">
    <path
      fillRule="evenodd"
      d="M10 5a.75.75 0 01.53.22l4 4a.75.75 0 11-1.06 1.06L10 6.81l-3.47 3.47a.75.75 0 11-1.06-1.06l4-4A.75.75 0 0110 5z"
      clipRule="evenodd"
    />
  </svg>
);
const ArrowDownIcon = (
  <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className="h-3.5 w-3.5">
    <path
      fillRule="evenodd"
      d="M10 15a.75.75 0 01-.53-.22l-4-4a.75.75 0 111.06-1.06L10 13.19l3.47-3.47a.75.75 0 111.06 1.06l-4 4A.75.75 0 0110 15z"
      clipRule="evenodd"
    />
  </svg>
);
const RemoveIcon = (
  <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className="h-3.5 w-3.5">
    <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
  </svg>
);

function move<T>(list: T[], index: number, delta: number): T[] {
  const target = index + delta;
  if (target < 0 || target >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(index, 1);
  next.splice(target, 0, item!);
  return next;
}

export default function StocksSettingsForm({
  widgetId,
  config,
  onSaved,
  formId,
}: {
  widgetId: WidgetId;
  config: Record<string, unknown>;
  onSaved: () => void;
  formId: string;
}) {
  const mutations = useDashboardWidgetMutations();
  const initial = useMemo(() => mergeStocksConfig(config as Partial<StocksWidgetConfig>), [config]);

  // Ordered selection: visible order first, then any remaining watchlist entries.
  const initialSelected = useMemo(() => {
    const order = [...initial.visible];
    for (const entry of initial.watchlist) if (!order.includes(entry.id)) order.push(entry.id);
    return order;
  }, [initial]);
  const initialCustoms = useMemo(() => {
    const map: Record<string, CustomAsset> = {};
    for (const entry of initial.watchlist) if (entry.custom) map[entry.id] = entry.custom;
    return map;
  }, [initial]);

  const [selectedIds, setSelectedIds] = useState<string[]>(initialSelected);
  const [customs, setCustoms] = useState<Record<string, CustomAsset>>(initialCustoms);
  const [tileSize, setTileSize] = useState(initial.tileSize);
  const [layout, setLayout] = useState<StocksLayout>(initial.layout);

  const [customSymbol, setCustomSymbol] = useState('');
  const [customLabel, setCustomLabel] = useState('');
  const [customError, setCustomError] = useState<string | null>(null);

  const symbolFieldId = useId();
  const labelFieldId = useId();
  const customErrId = useId();

  const labelFor = (id: string): string => {
    const known = KNOWN_ASSETS.find((a) => a.id === id);
    if (known) return known.label;
    return customs[id]?.label ?? id;
  };

  const availableKnown = KNOWN_ASSETS.filter((a) => !selectedIds.includes(a.id));
  const capacity = tileCapacity(tileSize, layout);

  const addKnown = (id: string) => setSelectedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  const remove = (id: string) => {
    setSelectedIds((prev) => prev.filter((v) => v !== id));
    setCustoms((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };
  const reorder = (index: number, delta: number) => setSelectedIds((prev) => move(prev, index, delta));

  const addCustom = () => {
    const symbol = customSymbol.trim().toUpperCase();
    const label = customLabel.trim();
    if (!CUSTOM_SYMBOL_RE.test(symbol)) {
      setCustomError('Symbol must be 1–20 chars: letters, digits, . - = ^');
      return;
    }
    if (!label) {
      setCustomError('Label is required.');
      return;
    }
    const id = customAssetId(symbol);
    if (selectedIds.includes(id)) {
      setCustomError('That ticker is already added.');
      return;
    }
    setCustoms((prev) => ({ ...prev, [id]: { symbol, label } }));
    setSelectedIds((prev) => [...prev, id]);
    setCustomSymbol('');
    setCustomLabel('');
    setCustomError(null);
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (mutations.isPending) return;
    const watchlist: WatchlistEntry[] = selectedIds.map((id) => (customs[id] ? { id, custom: customs[id] } : { id }));
    const values: StocksWidgetConfig = { watchlist, visible: selectedIds, tileSize, layout };
    mutations.updateConfig(widgetId, values as unknown as Record<string, unknown>);
    onSaved();
  };

  const sizeOptions = WIDGET_SIZES.map((size) => ({ value: size, label: size.replace('x', '×') }));

  return (
    <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
      {/* Selected assets, ordered */}
      <fieldset className="flex flex-col gap-2">
        <legend className="text-xs font-medium tracking-wide text-(--text-secondary) uppercase">Assets</legend>
        <p className="text-xs text-(--text-tertiary)">The tile shows the first {capacity} in this order; all appear in the detail view.</p>
        {selectedIds.length === 0 ? (
          <p className="rounded-md border border-dashed border-(--border-default) bg-(--bg-surface) px-3 py-4 text-xs text-(--text-tertiary)">
            No assets selected. Add some below.
          </p>
        ) : (
          <ul className="flex flex-col gap-1 rounded-md border border-(--border-subtle) bg-(--bg-surface) p-2">
            {selectedIds.map((id, index) => (
              <li key={id} className="flex items-center gap-2 rounded px-2 py-1 text-sm text-(--text-primary)">
                <span
                  className={cn(
                    'rounded px-1.5 text-[10px] font-medium',
                    index < capacity
                      ? 'bg-(--accent-primary-strong) text-(--text-on-accent)'
                      : 'bg-(--bg-surface-strong) text-(--text-tertiary)',
                  )}
                >
                  {index + 1}
                </span>
                <span className="min-w-0 flex-1 truncate">{labelFor(id)}</span>
                <button
                  type="button"
                  onClick={() => reorder(index, -1)}
                  disabled={index === 0}
                  aria-label={`Move ${labelFor(id)} up`}
                  className="rounded p-1 text-(--text-tertiary) hover:bg-(--bg-surface-hover) hover:text-(--text-primary) focus-visible:ring-2 focus-visible:ring-(--accent-primary) focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-30"
                >
                  {ArrowUpIcon}
                </button>
                <button
                  type="button"
                  onClick={() => reorder(index, 1)}
                  disabled={index === selectedIds.length - 1}
                  aria-label={`Move ${labelFor(id)} down`}
                  className="rounded p-1 text-(--text-tertiary) hover:bg-(--bg-surface-hover) hover:text-(--text-primary) focus-visible:ring-2 focus-visible:ring-(--accent-primary) focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-30"
                >
                  {ArrowDownIcon}
                </button>
                <button
                  type="button"
                  onClick={() => remove(id)}
                  aria-label={`Remove ${labelFor(id)}`}
                  className="rounded p-1 text-(--text-tertiary) hover:bg-(--bg-surface-hover) hover:text-(--accent-danger) focus-visible:ring-2 focus-visible:ring-(--accent-primary) focus-visible:outline-none"
                >
                  {RemoveIcon}
                </button>
              </li>
            ))}
          </ul>
        )}
        {availableKnown.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {availableKnown.map((asset) => (
              <button
                key={asset.id}
                type="button"
                onClick={() => addKnown(asset.id)}
                className="rounded-full border border-(--border-default) bg-(--bg-surface) px-3 py-1 text-xs text-(--text-secondary) transition-colors hover:bg-(--bg-surface-hover) hover:text-(--text-primary) focus-visible:ring-2 focus-visible:ring-(--accent-primary) focus-visible:outline-none"
              >
                + {asset.label}
              </button>
            ))}
          </div>
        )}
      </fieldset>

      {/* Add custom ticker */}
      <fieldset className="flex flex-col gap-2">
        <legend className="text-xs font-medium tracking-wide text-(--text-secondary) uppercase">Add custom ticker</legend>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            id={symbolFieldId}
            type="text"
            value={customSymbol}
            onChange={(e) => setCustomSymbol(e.target.value)}
            placeholder="Symbol (e.g. AAPL)"
            aria-label="Custom ticker symbol"
            aria-invalid={customError ? true : undefined}
            aria-describedby={customError ? customErrId : undefined}
            className={cn(inputClasses, 'sm:w-40')}
          />
          <input
            id={labelFieldId}
            type="text"
            value={customLabel}
            onChange={(e) => setCustomLabel(e.target.value)}
            placeholder="Label (e.g. Apple)"
            aria-label="Custom ticker label"
            className={cn(inputClasses, 'flex-1')}
          />
          <button
            type="button"
            onClick={addCustom}
            className="h-10 shrink-0 rounded-lg border border-(--border-default) bg-(--bg-surface-strong) px-4 text-sm font-medium text-(--text-primary) transition-colors hover:bg-(--bg-surface-hover) focus-visible:ring-2 focus-visible:ring-(--accent-primary) focus-visible:outline-none"
          >
            Add
          </button>
        </div>
        {customError && (
          <p id={customErrId} className="text-xs text-(--accent-danger)">
            {customError}
          </p>
        )}
      </fieldset>

      {/* Display */}
      <div className="flex flex-col gap-3">
        <Select
          label="Tile size"
          value={tileSize}
          onChange={(v) => setTileSize(v as StocksWidgetConfig['tileSize'])}
          options={sizeOptions}
        />
        <Switch
          label="Show a single large asset"
          checked={layout === 'single'}
          onChange={(checked) => setLayout(checked ? 'single' : 'grid')}
        />
      </div>
    </form>
  );
}
